import {IContextualMenuItem} from '@fluentui/react';
import {toast} from 'react-toastify';
import {useGetCompanyQuery} from '../../store/Api';
import {
    ICrmSyncResult,
    useGetCompanyLinkableFoliosQuery,
    useImportCompanyContactsMutation,
    useImportCompanyLocationsMutation
} from '../../store/endpoints/RecordLinksEndpoints';

/**
 * Three-dots menu items to synchronously sync a single company's locations/contacts from each CRM folio it's
 * linked to (saved externalMappings ∩ the company-linkable folios). Runs inline on ac-api (not the worker)
 * via the per-company import endpoints. Returns [] when the company isn't linked to any importable folio.
 */
export function useCompanyCrmSyncMenuItems(companyId?: string): IContextualMenuItem[] {
    const company = useGetCompanyQuery(companyId ?? '', {skip: !companyId});
    const linkable = useGetCompanyLinkableFoliosQuery();
    const [importLocations] = useImportCompanyLocationsMutation();
    const [importContacts] = useImportCompanyContactsMutation();

    if (!companyId) return [];
    const mappings = company.data?.externalMappings ?? {};
    const linkedFolios = (linkable.data ?? []).filter(f => mappings[f.folioId]);
    if (linkedFolios.length === 0) return [];

    const runSync = async (kind: 'locations' | 'contacts', folioId: string, folioName?: string) => {
        const noun = kind === 'locations' ? 'locations' : 'contacts';
        const label = folioName ?? 'CRM';
        const toastId = toast.loading(`Syncing ${noun} from ${label}…`, {closeButton: false, closeOnClick: false, draggable: false});
        const resolved = {isLoading: false, autoClose: 4000, closeButton: true, closeOnClick: true, draggable: true};
        try {
            const result: ICrmSyncResult = kind === 'locations'
                ? await importLocations({companyId, folioId}).unwrap()
                : await importContacts({companyId, folioId}).unwrap();
            const count = kind === 'locations' ? result.locations : result.contacts;
            if (result.success) {
                toast.update(toastId, {type: 'success', render: `${label}: ${count} ${noun} synced`, ...resolved});
            } else {
                toast.update(toastId, {type: 'error', render: result.errors?.[0] ?? `Failed to sync ${noun}`, ...resolved});
            }
        } catch {
            toast.update(toastId, {type: 'error', render: `Failed to sync ${noun} from ${label}`, ...resolved});
        }
    };

    const items: IContextualMenuItem[] = [];
    linkedFolios.forEach(f => {
        items.push({
            key: `sync-locations-${f.folioId}`,
            text: `Sync all Locations from ${f.folioName ?? 'CRM'}`,
            iconProps: {iconName: 'Sync'},
            onClick: () => { void runSync('locations', f.folioId, f.folioName); }
        });
        items.push({
            key: `sync-contacts-${f.folioId}`,
            text: `Sync all Contacts from ${f.folioName ?? 'CRM'}`,
            iconProps: {iconName: 'Sync'},
            onClick: () => { void runSync('contacts', f.folioId, f.folioName); }
        });
    });
    return items;
}
